// src/components/dashboards/charts/CohortSummaryCards.tsx
// KPI cards row: Total Patients, New This Month, Total Documents, Top Domain

import React from "react";
import { Box, Typography, alpha, useTheme, Skeleton } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DescriptionIcon from "@mui/icons-material/Description";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import { CohortSummary } from "hooks/useAnalytics";
import { useTranslation } from "../../../i18n";
import { athenaColors } from "../../landing/colors";
import { useDashboardInk } from "../dashboardInk";

interface Props {
  data: CohortSummary | null;
  loading: boolean;
  /** An environment filter is active, so the totals are NOT the whole cohort. */
  scoped?: boolean;
}

interface KpiCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  subtitle?: string;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ icon, label, value, subtitle, color }) => {
  const theme = useTheme();
  const ink = useDashboardInk();

  return (
    <Box
      sx={{
        flex: "1 1 0",
        minWidth: { xs: 130, sm: 160 },
        p: ink.dark ? { xs: 1.5, sm: 1.75 } : { xs: 1.5, sm: 2.5 },
        borderRadius: 2,
        // Hairline + blur, the same glass recipe the rest of the app uses,
        // so the cards separate from the surface they sit on.
        // Top-lit, like every other panel in the app: a flat 8% white wash
        // reads as a grey rectangle, the gradient reads as a lit surface.
        // The navy base underneath matters on the bubbles ground: a purely
        // white-tinted translucent card sitting over a bright blob lightens
        // instead of darkening, and takes its own white ink down with it.
        ...(ink.dark && { backgroundColor: alpha(athenaColors.deepNavy, 0.45) }),
        backgroundImage: ink.dark
          ? `linear-gradient(180deg, ${alpha(athenaColors.white, 0.11)} 0%, ${alpha(athenaColors.white, 0.04)} 100%)`
          : `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.055)} 0%, ${alpha(theme.palette.primary.dark, 0.03)} 100%)`,
        border: `1px solid ${
          ink.dark
            ? alpha(athenaColors.white, 0.14)
            : alpha(theme.palette.primary.dark, 0.1)
        }`,
        backdropFilter: "blur(8px)",
        display: "flex",
        flexDirection: "column",
        gap: 1,
        transition: "all 0.2s ease",
        "&:hover": {
          border: `1px solid ${alpha(color, ink.dark ? 0.6 : 0.4)}`,
          backgroundImage: ink.dark
            ? `linear-gradient(180deg, ${alpha(athenaColors.white, 0.15)} 0%, ${alpha(athenaColors.white, 0.06)} 100%)`
            : `linear-gradient(180deg, ${alpha(theme.palette.primary.dark, 0.09)} 0%, ${alpha(theme.palette.primary.dark, 0.05)} 100%)`,
          transform: "translateY(-2px)",
          boxShadow: `0 4px 20px ${alpha(color, ink.dark ? 0.25 : 0.12)}`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: ink.dark ? 2 : "50%",
            background: alpha(color, ink.dark ? 0.18 : 0.15),
            border: ink.dark ? `1px solid ${alpha(color, 0.35)}` : "none",
            // "Brillo": an outer bloom plus an inner top highlight, the same
            // two-part device the glass cards already use for their edges. On
            // the tile only, never on the label: a glow behind type costs
            // legibility, which is the one thing this surface cannot spend.
            //
            // 10px / 0.22 deliberately MATCHES the donut's shadowBlur and
            // shadowColor. The two are the only glowing things on the page, so
            // a tile blooming harder than the chart would read as an accident
            // rather than as one device.
            boxShadow: ink.dark
              ? `0 0 10px ${alpha(color, 0.22)}, inset 0 1px 0 ${alpha(
                  athenaColors.white,
                  0.18,
                )}`
              : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: ink.dark
              ? alpha(athenaColors.white, 0.7)
              : ink.secondary,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.65rem",
          }}
        >
          {label}
        </Typography>
      </Box>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          color: ink.dark
            ? athenaColors.white
            : ink.primary,
          lineHeight: 1.1,
        }}
      >
        {value}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          sx={{
            color: ink.dark
              ? alpha(athenaColors.white, 0.5)
              : ink.secondary,
            fontSize: "0.7rem",
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

const CohortSummaryCards: React.FC<Props> = ({ data, loading, scoped }) => {
  const ink = useDashboardInk();
  const theme = useTheme();
  const { t } = useTranslation();

  if (loading || !data) {
    return (
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {[0, 1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rounded"
            sx={{
              flex: "1 1 0",
              minWidth: 160,
              height: ink.dark ? 96 : 120,
              borderRadius: 2,
              ...(ink.dark && { bgcolor: alpha(athenaColors.white, 0.08) }),
            }}
          />
        ))}
      </Box>
    );
  }

  // On the dark ground these MUST come from the light family. Total Patients
  // was theme.palette.primary.main, which is deepNavy: a navy glyph in a navy
  // tile on a navy card, i.e. an invisible icon. Four distinct hues so the
  // cards stay tellable apart at a glance.
  const accent = ink.dark
    ? {
        patients: athenaColors.periwinkle,
        added: theme.palette.success.light,
        documents: athenaColors.lightPurple,
        domain: athenaColors.lightOrange,
      }
    : {
        patients: theme.palette.primary.main,
        added: theme.palette.success.main,
        documents: theme.palette.info.main,
        domain: theme.palette.info.main,
      };

  const topDomain = data.domains[0];
  const topDomainLabel = topDomain
    ? topDomain.domain.split(".").pop()?.replace(/_/g, " ") || "-"
    : "-";

  return (
    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
      <KpiCard
        icon={<PeopleIcon fontSize="small" />}
        label={t("dashboard.analytics.total_patients", "Total Patients")}
        value={data.total_patients.toLocaleString()}
        // Says what the number counts. "Across all environments" would be a
        // lie whenever the environment filter is on, which is most of the
        // time for anyone in more than one.
        subtitle={
          scoped
            ? t(
                "dashboard.analytics.total_patients_sub_scoped",
                "In the selected environments",
              )
            : t(
                "dashboard.analytics.total_patients_sub",
                "Across all environments",
              )
        }
        color={accent.patients}
      />
      <KpiCard
        icon={<TrendingUpIcon fontSize="small" />}
        label={t("dashboard.analytics.added_this_month", "Added This Month")}
        value={data.new_this_month.toLocaleString()}
        subtitle={t(
          "dashboard.analytics.added_this_month_sub",
          "records added to Athena",
        )}
        color={accent.added}
      />
      <KpiCard
        icon={<DescriptionIcon fontSize="small" />}
        label={t("dashboard.analytics.total_documents", "Documents")}
        value={data.total_documents.toLocaleString()}
        subtitle={t(
          "dashboard.analytics.total_documents_sub",
          "Clinical documents available",
        )}
        color={accent.documents}
      />
      <KpiCard
        icon={<LocalHospitalIcon fontSize="small" />}
        label={t("dashboard.analytics.top_domain", "Top Domain")}
        value={topDomainLabel.charAt(0).toUpperCase() + topDomainLabel.slice(1)}
        subtitle={topDomain ? `${topDomain.count} patients` : undefined}
        color={accent.domain}
      />
    </Box>
  );
};

export default CohortSummaryCards;
